import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  deleteReview,
  replyToReview,
  toggleReviewPublished,
} from '../../Store/reviews/reviewsSlice';
import styles from '../../styles/AdminPanel.module.scss';

const FILTER_OPTIONS = [
  { id: 'all', label: 'Усі' },
  { id: 'published', label: 'Опубліковані' },
  { id: 'hidden', label: 'Приховані' },
  { id: 'noReply', label: 'Без відповіді' },
  { id: 'withReply', label: 'З відповіддю' },
];

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const Stars = ({ rating }) => (
  <span className={styles.reviewsTableStars}>
    {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
  </span>
);

const ReviewsList = ({
  reviews = [],
  loading,
  error,
  onRefresh,
  onDelete,
  showMessage,
}) => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replyingId, setReplyingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const stats = useMemo(() => ({
    total: reviews.length,
    published: reviews.filter((r) => r.isPublished !== 0).length,
    noReply: reviews.filter((r) => !r.adminReply).length,
    avg: reviews.length
      ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
      : '0',
  }), [reviews]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return reviews.filter((review) => {
      const matchesSearch = !query || [
        review.productTitle,
        review.userName,
        review.userEmail,
        review.text,
        review.adminReply,
        String(review.id),
      ].some((v) => String(v || '').toLowerCase().includes(query));

      if (!matchesSearch) return false;

      switch (filter) {
        case 'published':
          return review.isPublished !== 0;
        case 'hidden':
          return review.isPublished === 0;
        case 'noReply':
          return !review.adminReply;
        case 'withReply':
          return Boolean(review.adminReply);
        default:
          return true;
      }
    });
  }, [reviews, search, filter]);

  const handleReply = async (review) => {
    const text = (replyDrafts[review.id] ?? review.adminReply ?? '').trim();
    if (!text) {
      showMessage?.('error', 'Введіть текст відповіді');
      return;
    }
    setReplyingId(review.id);
    try {
      await dispatch(replyToReview({ id: review.id, adminReply: text })).unwrap();
      showMessage?.('success', 'Відповідь збережено');
      setReplyDrafts((prev) => ({ ...prev, [review.id]: text }));
    } catch (err) {
      showMessage?.('error', err || 'Не вдалося зберегти відповідь');
    } finally {
      setReplyingId(null);
    }
  };

  const handleTogglePublish = async (review) => {
    try {
      await dispatch(toggleReviewPublished({
        id: review.id,
        isPublished: review.isPublished === 0,
      })).unwrap();
      showMessage?.('success', review.isPublished !== 0 ? 'Відгук приховано' : 'Відгук опубліковано');
    } catch (err) {
      showMessage?.('error', err || 'Не вдалося змінити статус');
    }
  };

  const handleDelete = (review) => {
    if (onDelete) {
      onDelete(review);
      return;
    }
    dispatch(deleteReview(review.id));
  };

  return (
    <div className={styles.reviewsListPage}>
      <div className={styles.reviewsListHeader}>
        <h2 className={styles.reviewsListTitle}>Відгуки покупців</h2>
        {onRefresh && (
          <button
            type="button"
            className={styles.reviewsListRefreshBtn}
            onClick={onRefresh}
            disabled={loading}
          >
            {loading ? 'Завантаження…' : 'Оновити'}
          </button>
        )}
      </div>

      {error && (
        <div className={`${styles.message} ${styles.error}`}>
          {typeof error === 'string' ? error : 'Не вдалося завантажити відгуки'}
        </div>
      )}

      <div className={styles.reviewsListStats}>
        <div className={styles.reviewsListStat}>
          <span className={styles.reviewsListStatLabel}>Усіго</span>
          <strong>{stats.total}</strong>
        </div>
        <div className={styles.reviewsListStat}>
          <span className={styles.reviewsListStatLabel}>Опубліковано</span>
          <strong>{stats.published}</strong>
        </div>
        <div className={styles.reviewsListStat}>
          <span className={styles.reviewsListStatLabel}>Без відповіді</span>
          <strong>{stats.noReply}</strong>
        </div>
        <div className={styles.reviewsListStat}>
          <span className={styles.reviewsListStatLabel}>Середня оцінка</span>
          <strong>{stats.avg}</strong>
        </div>
      </div>

      <div className={styles.reviewsListToolbar}>
        <select
          className={styles.reviewsListFilterSelect}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          {FILTER_OPTIONS.map((opt) => (
            <option key={opt.id} value={opt.id}>{opt.label}</option>
          ))}
        </select>
        <div className={styles.reviewsListSearch}>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Пошук за товаром, покупцем, текстом..."
          />
        </div>
      </div>

      {!reviews.length ? (
        <div className={styles.empty}>Відгуків поки немає</div>
      ) : (
        <div className={styles.reviewsTableWrap}>
          <table className={styles.reviewsTable}>
            <thead>
              <tr>
                <th>Товар</th>
                <th>Покупець</th>
                <th>Оцінка</th>
                <th>Відгук</th>
                <th>Відповідь</th>
                <th>Дата</th>
                <th>Дії</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className={styles.reviewsTableEmpty}>
                    За вашим запитом нічого не знайдено
                  </td>
                </tr>
              ) : (
                filtered.map((review) => {
                  const isExpanded = expandedId === review.id;
                  const draft = replyDrafts[review.id] ?? review.adminReply ?? '';

                  return (
                    <React.Fragment key={review.id}>
                      <tr className={review.isPublished === 0 ? styles.reviewsTableHidden : ''}>
                        <td className={styles.reviewsTableProduct}>
                          <strong>{review.productTitle}</strong>
                          <small>ID товара: {review.productId}</small>
                        </td>
                        <td className={styles.reviewsTableUser}>
                          <strong>{review.userName}</strong>
                          <small>{review.userEmail}</small>
                        </td>
                        <td><Stars rating={review.rating} /></td>
                        <td className={styles.reviewsTableText}>
                          <p>{review.text}</p>
                        </td>
                        <td className={styles.reviewsTableReplyCol}>
                          {review.adminReply ? (
                            <span className={styles.reviewsTableReplyBadge}>Є відповідь</span>
                          ) : (
                            <span className={styles.reviewsTableNoReply}>Немає відповіді</span>
                          )}
                        </td>
                        <td className={styles.reviewsTableDate}>{formatDate(review.createdAt)}</td>
                        <td className={styles.reviewsTableActions}>
                          <button
                            type="button"
                            onClick={() => setExpandedId(isExpanded ? null : review.id)}
                          >
                            {isExpanded ? 'Згорнути' : 'Відповісти'}
                          </button>
                          <button type="button" onClick={() => handleTogglePublish(review)}>
                            {review.isPublished !== 0 ? 'Приховати' : 'Показати'}
                          </button>
                          <button type="button" className={styles.reviewsTableDelete} onClick={() => handleDelete(review)}>
                            Видалити
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className={styles.reviewsTableReplyRow}>
                          <td colSpan={7}>
                            <div className={styles.reviewsReplyForm}>
                              <label>Відповідь магазину</label>
                              <textarea
                                rows={3}
                                value={draft}
                                onChange={(e) => setReplyDrafts((prev) => ({
                                  ...prev,
                                  [review.id]: e.target.value,
                                }))}
                                placeholder="Напишіть відповідь покупцю..."
                              />
                              <div className={styles.reviewsReplyActions}>
                                <button
                                  type="button"
                                  className={styles.reviewsReplySave}
                                  onClick={() => handleReply(review)}
                                  disabled={replyingId === review.id}
                                >
                                  {replyingId === review.id ? 'Збереження…' : 'Зберегти ответ'}
                                </button>
                                {review.adminReplyAt && (
                                  <span className={styles.reviewsReplyDate}>
                                    Відповідь від {formatDate(review.adminReplyAt)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className={styles.reviewsListFooter}>
        Показано {filtered.length} з {reviews.length}
      </div>
    </div>
  );
};

export default ReviewsList;
